import { Controller } from "@hotwired/stimulus"
import { v4 as uuidv4 } from "uuid"
import Sortable from "sortablejs"

const TYPE_LABELS = {
  new_products: 'New Products',
  top_products: 'Top Products',
  top_tags: 'Top Tags',
  category: 'Category',
  tag: 'Tag'
}

const TYPE_BADGE_CLASSES = {
  new_products: 'bg-cyan-600 text-cyan-100',
  top_products: 'bg-purple-600 text-purple-100',
  top_tags: 'bg-orange-600 text-orange-100',
  category: 'bg-emerald-600 text-emerald-100',
  tag: 'bg-emerald-600 text-emerald-100'
}

export default class extends Controller {
  static targets = ["sectionsContainer"]

  connect() {
    this.blurTimer = null
    this.loadData()
  }

  disconnect() {
    if (this.blurTimer) clearTimeout(this.blurTimer)
    if (this.sortable) this.sortable.destroy()
  }

  initSortable() {
    if (this.sortable) this.sortable.destroy()
    this.sortable = new Sortable(this.sectionsContainerTarget, {
      animation: 150,
      handle: '.handle',
      ghostClass: 'bg-slate-600',
      onEnd: () => this.updateOrders()
    })
  }

  loadData() {
    fetch('/admin/home_sections/data')
      .then(response => response.json())
      .then(data => {
        this.sectionsContainerTarget.innerHTML = ''
        if (data && data.length > 0) {
          data.sort((a, b) => a.order - b.order).forEach(section => this.renderSection(section))
        }
        this.initSortable()
      })
      .catch(error => console.error('Error loading home sections:', error))
  }

  renderSection(section) {
    const template = document.getElementById('section-template')
    const el = template.content.cloneNode(true).querySelector('.section-card')
    const contentType = (section.content && section.content.type) || 'new_products'

    el.dataset.sectionId = section.id
    el.dataset.contentType = contentType
    el.querySelector('.section-title').value = section.title || ''
    this.setBadge(el, contentType)

    if (contentType === 'category' || contentType === 'tag') {
      el.querySelector('.sectionable-fields').classList.remove('hidden')
      el.querySelector('.sectionable-id').value = (section.content && section.content.id) || ''
      this.resolveSectionableDisplay(el)
    }

    this.sectionsContainerTarget.appendChild(el)
  }

  setBadge(el, contentType) {
    const badge = el.querySelector('.section-type-badge')
    badge.textContent = TYPE_LABELS[contentType] || contentType
    badge.className = `section-type-badge px-2 py-0.5 rounded text-xs font-semibold ${TYPE_BADGE_CLASSES[contentType] || 'bg-slate-500 text-slate-100'}`
  }

  applyContentFields(el, content) {
    if (!content) return
    if (content.type === 'category' || content.type === 'tag') {
      el.querySelector('.sectionable-id').value = content.id || ''
    }
  }

  resolveSectionableDisplay(el) {
    const contentType = el.dataset.contentType
    const id = el.querySelector('.sectionable-id').value

    if (!['category', 'tag'].includes(contentType)) return
    if (!id) return

    const sectionableType = contentType === 'category' ? 'ProductCategory' : 'Tag'
    fetch(`/admin/home_sections/resolve?sectionable_type=${encodeURIComponent(sectionableType)}&id=${id}`)
      .then(r => r.json())
      .then(result => {
        if (result.error) {
          el.querySelector('.selected-display').innerHTML = this.missingDisplayHTML(id, contentType)
        } else {
          el.querySelector('.selected-display').innerHTML = this.selectedDisplayHTML(result)
        }
        el.querySelector('.selected-display').classList.remove('hidden')
      })
      .catch(err => console.error('Error resolving sectionable:', err))
  }

  selectedDisplayHTML(item) {
    const label = item.type === 'category' ? 'Category' : 'Tag'
    return `<span class="text-sm text-slate-300">${label}: ${this.escapeHtml(item.title)}</span>`
  }

  missingDisplayHTML(id, contentType) {
    const label = contentType === 'category' ? 'Category' : 'Tag'
    return `<span class="text-sm text-yellow-400">⚠ ${label} #${id} not found (may have been deleted)</span>`
  }

  escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  addSection(event) {
    const contentType = event.params.type
    const section = {
      id: `sec-${uuidv4()}`,
      title: '',
      content: { type: contentType },
      order: this.getNextOrder()
    }
    this.renderSection(section)
    this.initSortable()
  }

  removeSection(event) {
    event.target.closest('.section-card').remove()
    this.updateOrders()
  }

  moveUp(event) {
    const el = event.target.closest('.section-card')
    const prev = el.previousElementSibling
    if (prev) {
      this.sectionsContainerTarget.insertBefore(el, prev)
      this.updateOrders()
    }
  }

  moveDown(event) {
    const el = event.target.closest('.section-card')
    const next = el.nextElementSibling
    if (next) {
      this.sectionsContainerTarget.insertBefore(next, el)
      this.updateOrders()
    }
  }

  updateOrders() {
    this.sectionsContainerTarget.querySelectorAll('.section-card').forEach((el, i) => {
      el.dataset.order = i + 1
    })
  }

  getNextOrder() {
    return this.sectionsContainerTarget.querySelectorAll('.section-card').length + 1
  }

  showSearchResults(event) {
    const resultsBox = event.target.closest('.section-card').querySelector('.search-results')
    resultsBox.classList.remove('hidden')
    if (this.blurTimer) clearTimeout(this.blurTimer)
  }

  hideSearchResults(event) {
    const el = event.target.closest('.section-card')
    const resultsBox = el.querySelector('.search-results')
    const input = el.querySelector('.search-input')
    this.blurTimer = setTimeout(() => {
      resultsBox.classList.add('hidden')
    }, 200)
  }

  async doSearch(event) {
    const el = event.target.closest('.section-card')
    const q = event.target.value.trim()
    const contentType = el.dataset.contentType
    const resultsBox = el.querySelector('.search-results')

    if (!q) {
      resultsBox.innerHTML = '<div class="px-3 py-2 text-xs text-slate-400">Type to search...</div>'
      return
    }

    const sectionableType = contentType === 'category' ? 'ProductCategory' : 'Tag'
    try {
      const resp = await fetch(`/admin/home_sections/search?q=${encodeURIComponent(q)}&sectionable_type=${encodeURIComponent(sectionableType)}`)
      const results = await resp.json()
      if (results.length === 0) {
        resultsBox.innerHTML = '<div class="px-3 py-2 text-xs text-slate-400">No results found</div>'
      } else {
        resultsBox.innerHTML = results.map(item => `
          <button type="button"
            class="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-600 transition"
            data-action="mousedown->home-editor#selectResult"
            data-id="${item.id}"
            data-title="${this.escapeHtml(item.title)}"
            data-type="${item.type}">
            <span class="text-xs text-slate-400">${item.type === 'category' ? 'Category' : 'Tag'}:</span> ${this.escapeHtml(item.title)}
          </button>
        `).join('')
      }
    } catch (err) {
      console.error('Search failed:', err)
    }
  }

  selectResult(event) {
    event.preventDefault()
    const btn = event.target.closest('button')
    const id = btn.dataset.id
    const title = btn.dataset.title
    const type = btn.dataset.type
    const el = btn.closest('.section-card')

    el.querySelector('.sectionable-id').value = id
    el.querySelector('.search-results').classList.add('hidden')
    el.querySelector('.search-input').value = ''

    const displayBox = el.querySelector('.selected-display')
    displayBox.classList.remove('hidden')
    displayBox.innerHTML = `<span class="text-sm text-slate-300">${type === 'category' ? 'Category' : 'Tag'}: ${this.escapeHtml(title)}</span>`
  }

  collectData() {
    return [...this.sectionsContainerTarget.querySelectorAll('.section-card')].map((el, i) => {
      const contentType = el.dataset.contentType
      let content = { type: contentType }

      if (contentType === 'category' || contentType === 'tag') {
        const id = el.querySelector('.sectionable-id').value
        content.id = id ? parseInt(id) : null
      }

      return {
        id: el.dataset.sectionId,
        title: el.querySelector('.section-title').value,
        content: content,
        order: i + 1
      }
    })
  }

  save() {
    const data = this.collectData()

    fetch('/admin/home_sections', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ home_data: data })
    })
    .then(response => {
      if (response.ok) {
        alert('Home sections saved successfully!')
        return response.json()
      }
      throw new Error('Failed to save home sections')
    })
    .catch(error => {
      console.error('Error saving home sections:', error)
      alert('Failed to save home sections. Please try again.')
    })
  }
}
